import React from 'react'
import {
	Button,
	Popover,
	PopoverSurface,
	PopoverTrigger,
	Switch,
	Text,
	makeStyles,
	tokens
} from '@fluentui/react-components'
import { SparkleRegular, SparkleFilled } from '@fluentui/react-icons'
import type { EffectKey } from '../contexts/EffectsContext'
import { EFFECT_LABELS, useEffects } from '../contexts/EffectsContext'

const useStyles = makeStyles({
	surface: {
		display: 'flex',
		flexDirection: 'column',
		gap: '0.25em',
		padding: '1em',
		minWidth: '230px'
	},
	header: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: '0.5em',
		gap: '1em'
	},
	row: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between'
	},
	hint: {
		marginTop: '0.5em',
		color: tokens.colorNeutralForeground3,
		fontSize: '0.7rem',
		fontStyle: 'italic'
	}
})

/**
 * Control panel for the site's visual effects. Every animation is a
 * feature, not an accident — visitors choose their own intensity.
 */
export const EffectsPanel: React.FC = () => {
	const styles = useStyles()
	const { effects, anyEnabled, toggleEffect, setAll } = useEffects()

	return (
		<Popover positioning="below-end" withArrow>
			<PopoverTrigger disableButtonEnhancement>
				<Button
					appearance="subtle"
					icon={anyEnabled ? <SparkleFilled /> : <SparkleRegular />}
					aria-label="Visual effects settings"
					title="Visual effects"
				/>
			</PopoverTrigger>
			<PopoverSurface className={styles.surface}>
				<div className={styles.header}>
					<Text weight="semibold">Visual effects</Text>
					<Button
						appearance="subtle"
						size="small"
						onClick={() => setAll(!anyEnabled)}
					>
						{anyEnabled ? 'All off' : 'All on'}
					</Button>
				</div>
				{(Object.keys(EFFECT_LABELS) as EffectKey[]).map(key => (
					<div key={key} className={styles.row}>
						<Switch
							checked={effects[key]}
							onChange={() => toggleEffect(key)}
							label={EFFECT_LABELS[key]}
						/>
					</div>
				))}
				<Text className={styles.hint}>
					Complexity is a feature. Tune to taste.
				</Text>
			</PopoverSurface>
		</Popover>
	)
}
